import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDownIcon, CheckIcon } from '@untitledui-icons/react/line'
import { cn } from '@/lib/utils'
import { SUPPORTED_LOCALES, setLocale, type SupportedLocale } from '@/i18n'

export function OnboardingLanguageSelector() {
  const { i18n } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const currentLocale = i18n.language as SupportedLocale
  const currentLang = SUPPORTED_LOCALES.find((l) => l.code === currentLocale) || SUPPORTED_LOCALES[0]

  // Close on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const handleSelectLanguage = (code: SupportedLocale) => {
    setLocale(code)
    setIsOpen(false)
  }

  return (
    <div ref={containerRef} className="relative">
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'flex cursor-pointer items-center gap-1.5 rounded-lg border bg-white px-3 py-2 text-left shadow-[0px_1px_2px_0px_rgba(10,13,18,0.05)] transition-colors',
          isOpen
            ? 'border-[#d5d7da]'
            : 'border-[#d5d7da] hover:border-[#a4a7ae]'
        )}
      >
        <span className="text-base leading-none">{currentLang.flag}</span>
        <span className="text-sm font-medium text-[#414651]">{currentLang.name}</span>
        <ChevronDownIcon
          className={cn(
            'h-4 w-4 text-[#a4a7ae] transition-transform',
            isOpen && 'rotate-180'
          )}
        />
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div className="absolute right-0 top-full z-50 mt-1 w-[180px] overflow-hidden rounded-lg border border-[rgba(0,0,0,0.08)] bg-white py-1 shadow-[0px_12px_16px_-4px_rgba(10,13,18,0.08),0px_4px_6px_-2px_rgba(10,13,18,0.03)]">
          {SUPPORTED_LOCALES.map((lang) => {
            const isSelected = lang.code === currentLocale
            return (
              <button
                key={lang.code}
                type="button"
                onClick={() => handleSelectLanguage(lang.code)}
                className={cn(
                  'flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left transition-colors',
                  isSelected ? 'bg-[#fafafa]' : 'hover:bg-[#fafafa]'
                )}
              >
                <span className="text-base leading-none">{lang.flag}</span>
                <span className="flex-1 text-sm font-medium text-[#181d27]">
                  {lang.name}
                </span>
                {isSelected && <CheckIcon className="h-4 w-4 text-[#7f56d9]" />}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
